import React from 'react'

import { clsx } from 'clsx'

import { PlanType } from '$extensionSrc/__generated__/graphql'
import ActionButton, {
  ActionButtonType,
} from '$extensionSrc/components/ActionButton'
import { formatMonthDayAndYear } from '$extensionSrc/utils/dateUtils'

import UserSettingsRow from './UserSettingsRow'

import './UserSettingsPaidPlanRow.scss'

export type SubscriptionDetails = {
  status:
    | 'incomplete'
    | 'incomplete_expired'
    | 'trialing'
    | 'active'
    | 'past_due'
    | 'canceled'
    | 'unpaid'
    | 'paused'
  // Note: these timestamps are in seconds, but JS likes milliseconds
  currentPeriodEnd: number
  cancelAt: number | null
  endedAt: number | null
}

export default function UserSettingsPaidPlanRow({
  onUpgradeClick,
  planExpiresOn,
  planType,
  trialExpiresOn,
  hasEnterprisePlan,
  subscriptionDetails,
  onClickManageSubscription,
}: {
  onUpgradeClick: () => void
  planExpiresOn: string | null
  planType: PlanType
  trialExpiresOn: string | null
  hasEnterprisePlan: boolean
  subscriptionDetails: SubscriptionDetails
  onClickManageSubscription: () => void
}) {
  let userCategory:
    | 'NO_TRIAL_NO_PLAN'
    | 'ACTIVE_TRIAL'
    | 'EXPIRED_TRIAL'
    | 'ACTIVE_PLAN_NO_AUTO_RENEW'
    | 'ACTIVE_PLAN_AUTO_RENEW'
    | 'ACTIVE_PLAN_ENTERPRISE'
    | 'EXPIRED_PLAN'
    | 'PLAN_PAST_DUE'

  if (planType === PlanType.NoPlan) {
    if (!planExpiresOn && !trialExpiresOn) {
      userCategory = 'NO_TRIAL_NO_PLAN'
    } else if (!planExpiresOn && trialExpiresOn) {
      userCategory = 'EXPIRED_TRIAL'
    } else if (
      subscriptionDetails &&
      subscriptionDetails.status === 'past_due'
    ) {
      userCategory = 'PLAN_PAST_DUE'
    } else {
      userCategory = 'EXPIRED_PLAN'
    }
  } else if (planType === PlanType.TrialPlan) {
    userCategory = 'ACTIVE_TRIAL'
  } else if (planType === PlanType.StandardPlan) {
    if (hasEnterprisePlan) {
      userCategory = 'ACTIVE_PLAN_ENTERPRISE'
    } else if (
      !subscriptionDetails ||
      (subscriptionDetails && subscriptionDetails.cancelAt)
    ) {
      userCategory = 'ACTIVE_PLAN_NO_AUTO_RENEW'
    } else {
      userCategory = 'ACTIVE_PLAN_AUTO_RENEW'
    }
  } else {
    // Shouldn't get here
    userCategory = 'NO_TRIAL_NO_PLAN'
  }

  return (
    <div className="UserSettingsPaidPlanRow">
      <UserSettingsRow
        className={clsx(
          'paid-plan',
          planType === PlanType.StandardPlan && 'has-active-plan',
          'manage-plan-container',
        )}
      >
        <div className="d-flex flex-column">
          <div className="body1 bold">Your current plan</div>
          {userCategory === 'NO_TRIAL_NO_PLAN' && (
            <div className="body1 dark-gray">None</div>
          )}
          {userCategory === 'ACTIVE_TRIAL' && (
            <div>
              <div className="body1">Preview</div>
              <div className="body3 darkest-gray">
                Expires on {formatMonthDayAndYear(trialExpiresOn)}
              </div>
            </div>
          )}
          {userCategory === 'EXPIRED_TRIAL' && (
            <div>
              <div className="body1">Preview</div>
              <div className="body3 darker-red">
                Expired on {formatMonthDayAndYear(trialExpiresOn)}
              </div>
            </div>
          )}
          {userCategory === 'ACTIVE_PLAN_NO_AUTO_RENEW' && (
            <div>
              <div className="body1">Personal plan</div>
              <div className="body3 darker-red">
                Expires on {formatMonthDayAndYear(planExpiresOn)}
              </div>
            </div>
          )}
          {userCategory === 'ACTIVE_PLAN_AUTO_RENEW' && (
            <div>
              <div className="body1">Personal plan</div>
              <div className="body3 darkest-gray">
                Renews automatically on {formatMonthDayAndYear(planExpiresOn)}
              </div>
            </div>
          )}
          {userCategory === 'ACTIVE_PLAN_ENTERPRISE' && (
            <div>
              <div className="body1">Enterprise plan</div>
              <div className="body3 darkest-gray">
                Access through {formatMonthDayAndYear(planExpiresOn)}
              </div>
            </div>
          )}
          {userCategory === 'EXPIRED_PLAN' && (
            <div>
              <div className="body1">Personal plan</div>
              <div className="body3 darker-red">
                Expired on {formatMonthDayAndYear(planExpiresOn)}
              </div>
            </div>
          )}
          {userCategory === 'PLAN_PAST_DUE' && (
            <div>
              <div className="body1">Personal plan</div>
              <div className="body3 darker-red">
                Past due since {formatMonthDayAndYear(planExpiresOn)}
              </div>
            </div>
          )}
        </div>
        {userCategory === 'ACTIVE_PLAN_AUTO_RENEW' && (
          <ActionButton
            className="edit-button"
            onClick={onClickManageSubscription}
            type={ActionButtonType.TERTIARY}
          >
            Manage
          </ActionButton>
        )}
        {(userCategory === 'ACTIVE_PLAN_NO_AUTO_RENEW' ||
          (userCategory === 'PLAN_PAST_DUE' && subscriptionDetails)) && (
          <ActionButton
            className="edit-button"
            onClick={onClickManageSubscription}
            type={ActionButtonType.SECONDARY}
          >
            Manage
          </ActionButton>
        )}
        {userCategory === 'NO_TRIAL_NO_PLAN' && (
          <ActionButton
            className="edit-button"
            onClick={onUpgradeClick}
            type={ActionButtonType.SECONDARY}
          >
            Select...
          </ActionButton>
        )}
        {userCategory === 'ACTIVE_TRIAL' && (
          <ActionButton
            className="edit-button"
            onClick={onUpgradeClick}
            type={ActionButtonType.SECONDARY}
          >
            Upgrade
          </ActionButton>
        )}
        {userCategory === 'EXPIRED_TRIAL' && (
          <ActionButton
            className="edit-button"
            onClick={onUpgradeClick}
            type={ActionButtonType.PRIMARY}
          >
            Upgrade
          </ActionButton>
        )}
        {userCategory === 'EXPIRED_PLAN' && (
          <ActionButton
            className="edit-button"
            onClick={onUpgradeClick}
            type={ActionButtonType.SECONDARY}
          >
            Upgrade
          </ActionButton>
        )}
      </UserSettingsRow>
    </div>
  )
}
